import { config } from '../core/config.js';
import { utils } from '../core/utils.js';
import { eventBus } from '../core/eventBus.js';

export class Navigation {
  constructor() {
    this.header = document.querySelector('.header');
    this.navbar = document.querySelector('.navbar');
    this.mobileToggle = document.querySelector('.mobile-menu-toggle');
    this.navLinks = document.querySelector('.nav-links');
    this.navItems = document.querySelectorAll('.nav-links a');
    this.isMenuOpen = false;
    this.lastScrollY = 0;
    
    this.init();
  }
  
  init() {
    if (!this.header || !this.navbar) return;

    this.bindEvents();
    this.setupStickyHeader();
    this.setupActiveLinks();
    this.setupMobileMenu();
  }
  
  bindEvents() {
    // Mobile menu toggle
    this.mobileToggle?.addEventListener('click', () => this.toggleMobileMenu());
    
    // Navigation clicks with smooth scroll
    this.navLinks?.addEventListener('click', (e) => this.handleNavClick(e));
    
    // Close mobile menu on outside click
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    // Window resize
    window.addEventListener('resize', utils.debounce(() => this.handleResize(), 250));
  }
  
  setupStickyHeader() {
    const scrollHandler = utils.throttle(() => {
      const currentScrollY = window.scrollY;

      // Add/remove scrolled class
      if (currentScrollY > 50) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }

      this.lastScrollY = currentScrollY;
      this.updateActiveNavLink();
    }, 100);

    window.addEventListener('scroll', scrollHandler);
  }
  
  setupActiveLinks() {
    // Set active link on page load
    this.updateActiveNavLink();
  }
  
  updateActiveNavLink() {
    const scrollPosition = window.scrollY + 100;
    
    document.querySelectorAll('section[id]').forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const sectionId = section.getAttribute('id');
      
      if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
        this.navItems.forEach(item => {
          item.classList.remove('active');
          if (item.getAttribute('href') === `#${sectionId}`) {
            item.classList.add('active');
          }
        });
      }
    });
  }
  
  handleNavClick(e) {
    const target = e.target.closest('a');
    if (!target || !target.hash) return;
    
    e.preventDefault();
    const targetId = target.hash;
    const targetSection = document.querySelector(targetId);
    
    if (targetSection) {
      // Close mobile menu if open
      if (this.isMenuOpen) {
        this.toggleMobileMenu();
      }
      
      // Smooth scroll
      utils.smoothScroll(targetId, config.animations.scrollOffset);
      
      // Update URL without triggering scroll
      history.pushState(null, null, targetId);
      
      // Emit navigation event
      eventBus.emit('navigation:click', { target: targetId });
    }
  }

  setupMobileMenu() {
    // Prevent body scroll when menu is open
    const toggleBodyScroll = (disable) => {
        if (disable) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        }
    };
    
    // Handle menu state changes
    eventBus.on('mobile-menu:toggle', ({ isOpen }) => {
        toggleBodyScroll(isOpen);
    });
    
    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isMenuOpen) {
        this.toggleMobileMenu();
        }
    });
    
    // Handle swipe gestures for mobile
    if ('ontouchstart' in window) {
        let touchStartX = 0;
        let touchEndX = 0;
        
        this.navLinks?.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });
        
        this.navLinks?.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipeGesture(touchStartX, touchEndX);
        }, { passive: true });
    }
    
    // Add ARIA attributes
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    this.mobileToggle?.setAttribute('aria-controls', 'nav-links');
    this.navLinks?.setAttribute('id', 'nav-links');
    this.navLinks?.setAttribute('aria-hidden', 'true');
    
    // Setup focus trap when menu is open
    this.setupFocusTrap();
  }

handleSwipeGesture(startX, endX) {
  const swipeThreshold = 50;
  const diff = startX - endX;
  
  // Swipe left to close
  if (diff > swipeThreshold && this.isMenuOpen) {
    this.toggleMobileMenu();
  }
}

setupFocusTrap() {
  const focusableElements = this.navLinks?.querySelectorAll(
    'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
  );
  
  if (!focusableElements || focusableElements.length === 0) return;
  
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];
  
  this.navLinks?.addEventListener('keydown', (e) => {
    if (!this.isMenuOpen) return;
    
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    }
  });
}
  
  toggleMobileMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    
    this.navLinks?.classList.toggle('active', this.isMenuOpen);
    this.mobileToggle?.classList.toggle('active', this.isMenuOpen);
    document.body.classList.toggle('menu-open', this.isMenuOpen);
    
    // Update ARIA attributes
    this.mobileToggle?.setAttribute('aria-expanded', this.isMenuOpen.toString());
    this.navLinks?.setAttribute('aria-hidden', (!this.isMenuOpen).toString());
    
    // Animate hamburger
    this.animateHamburger();
    
    // Focus management
    if (this.isMenuOpen) {
        // Set focus to first menu item when opening
        setTimeout(() => {
        const firstLink = this.navLinks?.querySelector('a');
        firstLink?.focus();
        }, 300);
    } else {
        // Return focus to toggle button when closing
        this.mobileToggle?.focus();
    }
    
    // Emit event
    eventBus.emit('mobile-menu:toggle', { isOpen: this.isMenuOpen });
  }
  
  animateHamburger() {
    const spans = this.mobileToggle?.querySelectorAll('span');
    if (!spans) return;
    
    if (this.isMenuOpen) {
      spans[0].style.transform = 'translateY(7px) rotate(45deg)';
      spans[1].style.opacity = '0';
      spans[2].style.transform = 'translateY(-7px) rotate(-45deg)';
    } else {
      spans[0].style.transform = '';
      spans[1].style.opacity = '';
      spans[2].style.transform = '';
    }
  }
  
  handleOutsideClick(e) {
    if (this.isMenuOpen && 
        !this.navLinks?.contains(e.target) && 
        !this.mobileToggle?.contains(e.target)) {
      this.toggleMobileMenu();
    }
  }
  
  handleResize() {
    // Close mobile menu on resize to desktop
    if (window.innerWidth > config.breakpoints.tablet && this.isMenuOpen) {
      this.toggleMobileMenu();
    }
  }
  
  destroy() {
    // Clean up event listeners if needed
    window.removeEventListener('scroll', this.updateActiveNavLink);
    window.removeEventListener('resize', this.handleResize);
  }
}